import { useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { PublicHeader } from '@/components/organisms/public-header'
import { SiteFooter } from '@/components/organisms/SiteFooter'
import { TokenGate } from '@/components/organisms/token-gate'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { useAuth } from '@/hooks/useAuth'
import { useRequestNewLink } from '@/hooks/useRequestNewLink'

/**
 * Task 47 · «404 med tre utfall etter adressen» — frame 55.
 *
 * The file was 33 lines with ONE outcome and one way out, and the way out went
 * to `/`. Two things were wrong with that, and the second is the worse one:
 *
 *  1. Every 404 said the same thing to everybody. A person whose invitation
 *     link died and a person who mistyped a URL got identical words.
 *  2. `PublicHeader` rendered unconditionally, so a signed-in organiser who
 *     mistyped an address met the MARKETING top bar — «Kom i gang» — for a
 *     product she has already bought. Frame 55 states the rule: «Aldri
 *     "tilbake til forsiden" for en innlogget bruker — forsiden er
 *     markedsføring for et produkt hun alt har kjøpt.»
 *
 * So the address decides, in this order:
 *
 *  · **It looks like a token link** → yellow, and it offers a NEW one. This
 *    outranks being signed in: a dead invitation is a dead invitation whether
 *    or not you happen to have an account.
 *  · **Signed in** → indigo, «Til arrangementene». Never `/`, never
 *    `PublicHeader`.
 *  · **Signed out, ordinary address** → neutral, «Logg inn». Frame 55 on this
 *    one: «Det eneste tilfellet der forsiden er en ærlig utvei: vi vet ikke
 *    hvem dette er, og har ingenting bedre å tilby.»
 *
 * **No new route, no new table, no new component.** The gate is task 7's
 * `TokenGate` and the write is task 9's `request_link_reissue`, exactly as
 * frame 55's own note requires.
 */

/**
 * The eight token routes, by prefix. A wrong token still MATCHES its route and
 * is handled there — this catch-all only sees addresses that match no route at
 * all, which for a token link means a mangled one: a mail client that wrapped
 * the URL and dropped the last segment, or a share that truncated it. That is
 * precisely the person who needs a new link and has no other way to ask.
 */
const TOKEN_ROUTE_PREFIXES = [
  '/event/public',
  '/offer',
  '/confirm-offer',
  '/poll',
  '/invite',
  '/form/respond',
  '/form/f',
  '/p',
]

function looksLikeTokenLink(pathname: string): boolean {
  return TOKEN_ROUTE_PREFIXES.some(
    (p) => pathname === p || pathname.startsWith(`${p}/`),
  )
}

export function NotFound() {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const { user } = useAuth()
  useDocumentTitle(t('docTitle.notFound'))

  const { asking, asked, request: requestNewLink } = useRequestNewLink()

  const isTokenLink = useMemo(() => looksLikeTokenLink(pathname), [pathname])


  const gate = isTokenLink ? (
    <TokenGate
      outcome="expired"
      title={t('notFoundPage.tokenTitle')}
      description={asked ? t('notFoundPage.tokenAsked') : t('notFoundPage.tokenBody')}
      action={
        asked
          ? { label: t('notFoundPage.strangerAlt'), to: '/' }
          : { label: asking ? t('common.loading') : t('notFoundPage.tokenCta'), onClick: () => void requestNewLink() }
      }
      secondaryAction={asked ? undefined : { label: t('notFoundPage.tokenAlt'), to: '/' }}
    />
  ) : user ? (
    <TokenGate
      outcome="not-logged-in"
      title={t('notFoundPage.title')}
      description={t('notFoundPage.signedInBody')}
      action={{ label: t('notFoundPage.signedInCta'), to: '/events' }}
      secondaryAction={{ label: t('notFoundPage.signedInAlt'), to: '/dashboard' }}
    />
  ) : (
    <TokenGate
      outcome="not-logged-in"
      title={t('notFoundPage.title')}
      description={t('notFoundPage.strangerBody')}
      action={{ label: t('notFoundPage.strangerCta'), to: '/login' }}
      secondaryAction={{ label: t('notFoundPage.strangerAlt'), to: '/' }}
    />
  )

  return (
    <div className="flex h-full min-h-dvh flex-col bg-[var(--surface-page)]">
      {/* The marketing header renders for ONE of the three cases: a signed-out
          visitor on an ordinary address, where we genuinely do not know who
          this is. A signed-in user must never see it (the ferdig-sentence says
          so), and neither should someone holding a dead invitation — «Kom i
          gang» is the wrong offer to a person who was already invited. */}
      {!user && !isTokenLink && <PublicHeader />}
      <main
        id="main-content"
        className="mx-auto flex w-full max-w-[var(--content-width)] flex-1 flex-col justify-center px-4 py-16"
      >
        {/* The outcome card at the width the invite pages give it: bare in a 960px main, its
            full-width buttons stretched to 928px under a short heading (30.10.2026). */}
        <div className="mx-auto w-full max-w-md">{gate}</div>
      </main>
      <SiteFooter />
    </div>
  )
}
